<script setup lang="ts">
import type { MediaFragment, StaffMemberCategory, StaffMemberThumbFragment } from '#graphql-operations'

interface CardStaffProps {
  data: StaffMemberThumbFragment
}

const props = defineProps<CardStaffProps>()

const thumbMedia = computed<MediaFragment | null>(() => {
  const media = props.data.thumbMedia.find((item): item is MediaFragment => item?.kind === 'image')
  return media ?? null
})

const categories = computed(() => props.data.staffMemberCategory as StaffMemberCategory[])

const colorScheme = useColorScheme(categories.value[0]?.colorScheme)
</script>

<template>
  <article class="cardStaff group group-buttonLink c-$color-scheme-foreground rounded-2.5 bg-$color-scheme-background flex flex-col h-full relative of-hidden" :class="colorScheme">
    <figure class="bg-$color-scheme-foreground w-full aspect-1 of-hidden">
      <CraftMedia
        v-if="thumbMedia"
        :key="thumbMedia.id"
        class="motion-relaxed size-full object-cover group-hover-media-mouse:scale-105"
        :media="thumbMedia"
        :img-props="{ alt: data.title }"
      />
    </figure>
    <div class="px-5 py-7.5 flex flex-1 flex-col items-start space-y-10">
      <div v-if="data.role || data.title" class="space-y-2">
        <Text v-if="data.role" as="p" :text="data.role" class="typo-sans-1 min-h-10 uppercase" />
        <Text v-if="data.title" as="h4" :text="data.title" class="typo-serif-2 lt-lg:text-6.25 lt-lg:leading-7.25" />
      </div>
      <div v-if="data.excerpt" class="space-y-2">
        <Text as="p" :text="$t('bio')" class="typo-sans-1 uppercase" />
        <Text v-if="data.excerpt" :html="data.excerpt" class="typo-serif-1 line-clamp-3" />
      </div>
      <ButtonLink as="span" class="mt-auto" :title="$t('discover_profile')" />
    </div>
    <Anchor v-if="data.url" :to="data.url" class="inset-0 absolute z-10" />
  </article>
</template>
